// Import bootstrap first (sets up axios, moment, echo, etc.)
import './bootstrap';

// Import globals BEFORE any components (sets up window utilities)
import './globals';

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { useOrgStore } from '@/stores/org';
import { usePortalStore } from '@/stores/portal';
import { provideMoneyContext } from '@/utils/money';
import App from './App.vue';
import router from './routes';
import i18n from './i18n';
import Buefy from 'buefy';
// Fixes dropdown orientation of b-autocomplete/b-taginput inside modals
import './utils/buefyDropdownFix';
// Buefy CSS is now compiled from SCSS in vendor.scss with custom Bulma variables
// MDI fonts are imported via vendor.scss

// External CSS (moved from vendor.scss for Vite compatibility)
import '@mdi/font/css/materialdesignicons.css';

// Import filters
import filters from './filters';

// Import component registration
import { registerComponents } from './components';
import { vMaska } from 'maska/vue';
import { resizableCols } from './directives/resizableCols';
import { dragScroll } from './directives/dragScroll';
import { initSentry } from './utils/sentry';

// // Import MdiIcon for Buefy configuration
// import MdiIcon from './components/MdiIcon.vue'

// Create Vue app
const app = createApp(App);

// Initialize Sentry as early as possible (no-op unless a DSN is configured)
initSentry(app, router);

// Register MdiIcon first (needed for Buefy)
// app.component('mdi-icon', MdiIcon)

// Use plugins
app.use(createPinia());

// The formatter cannot import the stores — it has to stay loadable without a
// bundler — so it is handed the active organization here, once Pinia exists.
// The portal keeps its own store and never fills the app's; the platform
// console fills neither, and falls through to Elixio's own back office.
provideMoneyContext(() => {
    const portal = usePortalStore().organization;

    if (portal?.currency_code) {
        return { region: portal.region, currency: portal.currency_code, country: portal.country };
    }

    const org = useOrgStore();

    // The slug, not the currency: the store starts life holding 'CAD', so a
    // currency alone cannot tell a real organization from an empty store.
    return org.slug ? { region: org.region, currency: org.currency_code, country: org.country } : null;
});
app.use(router);
app.use(i18n);
app.use(Buefy, {
    defaultIconPack: 'mdi'
    // defaultIconComponent: 'mdi-icon'
});

// Expose Buefy's programmatic toast as globals for legacy/module code.
// Buefy only registers `$buefy.toast` (with an `.open()` method) on the app
// instance — it never sets a `window.Toast` / `window.ToastProgrammatic`, so
// bare `Toast.success(...)` and `window.ToastProgrammatic?.open(...)` calls
// scattered across the app were silently no-opping. Wire them here once.
const buefyToast = app.config.globalProperties.$buefy.toast;
window.ToastProgrammatic = buefyToast;
window.Toast = {
    open: options => buefyToast.open(options),
    success: message => buefyToast.open({ message, type: 'is-success' }),
    error: message => buefyToast.open({ message, type: 'is-danger' }),
    warning: message => buefyToast.open({ message, type: 'is-warning' }),
    info: message => buefyToast.open({ message, type: 'is-info' })
};

// Same story for snackbar/notification: the singletons imported from `buefy`
// aren't wired to this app instance and no-op, so expose the working ones.
window.SnackbarProgrammatic = app.config.globalProperties.$buefy.snackbar;
window.NotificationProgrammatic = app.config.globalProperties.$buefy.notification;

// Register global components
registerComponents(app);
app.directive('maska', vMaska);
app.directive('resizable-cols', resizableCols);
app.directive('drag-scroll', dragScroll);

// Make filters available globally
app.config.globalProperties.$filters = filters;

// Make i18n available globally for legacy code
window.i18n = i18n.global;

// Expose for debugging and legacy code
window.router = router;
window.store = app.config.globalProperties.$pinia;

// Mount the app
app.mount('#app');

// Keep the API-token cookie fresh while the user is active so the session never
// expires mid-use. The /heartbeat web route runs CreateFreshApiToken, which
// re-issues the cookie. Only ping when the tab is visible and we're in the org
// app (the portal manages its own bearer-token auth).
const HEARTBEAT_INTERVAL = 20 * 60 * 1000; // 20 min, well under the session lifetime
const inAppContext = () => {
    const path = window.location.pathname;
    return path !== '/login' && !path.startsWith('/portal');
};
setInterval(() => {
    if (document.visibilityState === 'visible' && inAppContext()) {
        window.axios.get('/heartbeat').catch(() => {});
    }
}, HEARTBEAT_INTERVAL);

// Dev: keep #app from being covered by the PHP Debug Bar (fixed, bottom: 0).
// The debug bar is injected asynchronously, so watch for it.
const watchDebugBar = () => {
    const bar = document.querySelector('.phpdebugbar');
    if (!bar) return false;
    const setOffset = () => {
        document.documentElement.style.setProperty('--dev-bar-height', `${bar.getBoundingClientRect().height}px`);
    };
    setOffset();
    new ResizeObserver(setOffset).observe(bar);
    return true;
};
if (!watchDebugBar()) {
    const mo = new MutationObserver(() => {
        if (watchDebugBar()) mo.disconnect();
    });
    mo.observe(document.body, { childList: true, subtree: true });
}

export default app;
